import RAZORPAY_CONFIG from '../config/razorpay.js';

// Razorpay Service for EmailValidator
class RazorpayService {
  constructor() {
    this.razorpay = null;
    this.config = RAZORPAY_CONFIG;
    this.initializeRazorpay();
  }

  initializeRazorpay() {
    // Load Razorpay script dynamically
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      this.razorpay = window.Razorpay;
    };
    document.head.appendChild(script);
  }

  // Create order on your backend
  async createOrder(planDetails, billingCycle) {
    try {
      // For testing without Supabase functions, create a mock order
      const mockOrder = {
        id: `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        amount: this.getPlanAmount(planDetails, billingCycle),
        currency: planDetails.currency || this.config.DEFAULT_CURRENCY,
        receipt: `receipt_${Date.now()}`,
      };

      // Try to call Supabase function, but fallback to mock if it fails
      try {
        const response = await fetch('/functions/v1/create-razorpay-order', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            plan: planDetails.name,
            billingCycle,
            amount: this.getPlanAmount(planDetails, billingCycle),
            currency: planDetails.currency || this.config.DEFAULT_CURRENCY, // Use plan currency or default
          }),
        });

        if (response.ok) {
          const orderData = await response.json();
          return orderData;
        } else {
          console.warn('Supabase function not available, using mock order');
          return mockOrder;
        }
      } catch (fetchError) {
        console.warn('Supabase function not available, using mock order:', fetchError);
        return mockOrder;
      }
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  // Get plan amount in smallest currency unit (Razorpay expects amount in smallest currency unit)
  getPlanAmount(planDetails, billingCycle) {
    // If planDetails already has amount and currency, use those
    if (planDetails.amount && planDetails.currency) {
      return planDetails.amount;
    }
    
    // Fallback to default USD pricing
    let amount = 0;
    
    if (planDetails.name === 'Pro') {
      amount = billingCycle === 'monthly' ? 1400 : 14000; // $14/month or $140/year (in cents)
    } else if (planDetails.name === 'Enterprise') {
      amount = billingCycle === 'monthly' ? 4900 : 49000; // $49/month or $490/year (in cents)
    }
    
    return amount; // Amount in smallest currency unit
  }

  // Initialize payment
  async initiatePayment(planDetails, billingCycle, userEmail) {
    try {
      // Create order first
      const orderData = await this.createOrder(planDetails, billingCycle);
      
      if (!this.razorpay) {
        throw new Error('Razorpay not loaded');
      }

      const options = {
        key: this.config.KEY_ID, // Your Razorpay Key ID
        amount: orderData.amount,
        currency: orderData.currency,
        name: this.config.BRAND_NAME,
        description: `${planDetails.name} Plan - ${billingCycle === 'monthly' ? 'Monthly' : 'Yearly'} Billing`,
        order_id: orderData.id,
        prefill: {
          email: userEmail,
        },
        theme: {
          color: this.config.BRAND_COLOR, // Your brand color
        },
        handler: (response) => {
          this.handlePaymentSuccess(response, planDetails, billingCycle);
        },
        modal: {
          ondismiss: () => {
            this.handlePaymentDismiss();
          },
        },
        // Enhanced payment methods for USD
        config: {
          display: {
            blocks: {
              banks: {
                name: "Pay using Cards & UPI",
                instruments: [
                  {
                    method: "card",
                    issuers: ["VISA", "MASTERCARD", "AMEX", "DISCOVER"]
                  },
                  {
                    method: "netbanking",
                    banks: ["ICIC", "HDFC", "SBIN", "AXIS", "KOTAK"]
                  },
                  {
                    method: "upi"
                  }
                ]
              },
              digitalWallets: {
                name: "Digital Wallets",
                instruments: [
                  {
                    method: "applepay"
                  },
                  {
                    method: "googlepay"
                  }
                ]
              },
              paypal: {
                name: "PayPal",
                instruments: [
                  {
                    method: "paypal"
                  }
                ]
              }
            }
          }
        }
      };

      const rzp = new this.razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Payment initiation failed:', error);
      throw error;
    }
  }

  // Redirect to Razorpay hosted payment page
  async redirectToPayment(planDetails, billingCycle, userEmail) {
    try {
      // Create order first
      const orderData = await this.createOrder(planDetails, billingCycle);
      
      // Create Razorpay payment link with return URL
      const paymentUrl = `https://checkout.razorpay.com/v1/pay/${orderData.id}?redirect_url=${encodeURIComponent(this.config.RETURN_URL)}`;
      
      // Redirect to Razorpay hosted page
      window.location.href = paymentUrl;
    } catch (error) {
      console.error('Payment redirect failed:', error);
      throw error;
    }
  }

  // Handle payment return from Razorpay
  async handlePaymentReturn() {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentId = urlParams.get('razorpay_payment_id');
    const orderId = urlParams.get('razorpay_order_id');
    const signature = urlParams.get('razorpay_signature');
    
    if (paymentId && orderId && signature) {
      // Payment was successful
      try {
        // Verify payment on backend
        const verificationResponse = await fetch('/functions/v1/verify-payment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            razorpay_payment_id: paymentId,
            razorpay_order_id: orderId,
            razorpay_signature: signature,
          }),
        });

        if (verificationResponse.ok) {
          const verificationData = await verificationResponse.json();
          if (verificationData.success) {
            alert('Payment successful! Your subscription has been activated.');
            window.location.href = '/account-settings';
          } else {
            alert('Payment verification failed. Please contact support.');
          }
        } else {
          console.warn('Payment verification endpoint not available, simulating success');
          alert('Payment successful! Your subscription has been activated.');
          window.location.href = '/account-settings';
        }
      } catch (error) {
        console.error('Payment verification failed:', error);
        alert('Payment verification failed. Please contact support.');
      }
    } else if (urlParams.get('error')) {
      // Payment failed
      alert('Payment failed. Please try again.');
      window.location.href = '/pricing';
    }
  }

  // Handle successful payment
  async handlePaymentSuccess(response, planDetails, billingCycle) {
    try {
      // For testing without Supabase functions, simulate successful payment
      console.log('Payment successful:', response);
      
      // Try to verify payment on backend, but fallback for testing
      try {
        const verificationResponse = await fetch('/functions/v1/verify-payment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            plan: planDetails.name,
            billingCycle,
          }),
        });

        if (verificationResponse.ok) {
          const verificationData = await verificationResponse.json();
          
          if (verificationData.success) {
            // Update user subscription in your database
            await this.updateUserSubscription(planDetails, billingCycle);
            
            // Show success message
            this.showSuccessMessage(planDetails, billingCycle);
            
            // Redirect to dashboard or account settings
            window.location.href = '/account-settings';
          } else {
            throw new Error('Payment verification failed');
          }
        } else {
          console.warn('Payment verification endpoint not available, simulating success');
          this.showSuccessMessage(planDetails, billingCycle);
          window.location.href = '/account-settings';
        }
      } catch (verificationError) {
        console.warn('Payment verification failed, simulating success for testing:', verificationError);
        this.showSuccessMessage(planDetails, billingCycle);
        window.location.href = '/account-settings';
      }
    } catch (error) {
      console.error('Payment verification failed:', error);
      this.showErrorMessage('Payment verification failed. Please contact support.');
    }
  }

  // Update user subscription
  async updateUserSubscription(planDetails, billingCycle) {
    try {
      const response = await fetch('/api/update-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan: planDetails.name,
          billingCycle,
          status: 'active',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update subscription');
      }

      return await response.json();
    } catch (error) {
      console.error('Subscription update failed:', error);
      throw error;
    }
  }

  // Handle payment dismiss
  handlePaymentDismiss() {
    console.log('Payment modal dismissed');
    // You can show a message or redirect user
  }

  // Show success message
  showSuccessMessage(planDetails, billingCycle) {
    // You can use a toast notification library or custom alert
    alert(`Payment successful! You are now subscribed to the ${planDetails.name} plan (${billingCycle} billing).`);
  }

  // Show error message
  showErrorMessage(message) {
    alert(`Payment failed: ${message}`);
  }

  // Get supported currencies
  getSupportedCurrencies() {
    return this.config.SUPPORTED_CURRENCIES;
  }

  // Convert amount to different currency (you'll need to implement currency conversion)
  convertCurrency(amount, fromCurrency, toCurrency) {
    // This is a placeholder - you'll need to implement actual currency conversion
    // You can use external APIs like exchangerate-api.com or currencylayer.com
    const conversionRates = {
      'INR': 1,
      'USD': 0.012,
      'EUR': 0.011,
      'GBP': 0.0095,
      'CAD': 0.016,
      'AUD': 0.018,
      'SGD': 0.016,
      'AED': 0.044,
    };

    const inrAmount = amount / conversionRates[fromCurrency];
    return Math.round(inrAmount * conversionRates[toCurrency]);
  }
}

export default new RazorpayService(); 